import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api from "../services/api";

const PhoneAccountContext = createContext(null);

/**
 * PhoneAccount Provider Component
 * Manages phone accounts and selected phoneId state (no localStorage)
 */
export const PhoneAccountProvider = ({ children }) => {
  const [phoneAccounts, setPhoneAccounts] = useState([]);
  const [selectedPhoneId, setSelectedPhoneId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Use ref to track selectedPhoneId to avoid dependency issues
  const selectedPhoneIdRef = useRef(selectedPhoneId);
  useEffect(() => {
    selectedPhoneIdRef.current = selectedPhoneId;
  }, [selectedPhoneId]);

  /**
   * Fetch available phone accounts from API
   * Uses centralized API service with interceptors
   * @param {string} [section] - Optional section name to filter phones by agent permissions
   */
  const fetchPhoneAccounts = useCallback(async (section) => {
    try {
      setLoading(true);
      
      // Check if token exists
      const token = localStorage.getItem("token");
      if (!token) {
        setPhoneAccounts([]);
        return;
      }

      // Use centralized API service (interceptor handles token)
      const params = {};
      if (section) params.section = section;
      const response = await api.get("/api/whatsapp-accounts", { params });

      const data = response.data;
      if (data.success && data.data && Array.isArray(data.data)) {
        const flattened = [];

        data.data.forEach((account) => {
          if (
            account.phone_numbers &&
            Array.isArray(account.phone_numbers) &&
            account.phone_numbers.length > 0
          ) {
            account.phone_numbers.forEach((phone) => {
              flattened.push({
                id: account.id,
                wabaId: account.id || account.waba_id,
                wabaName:
                  account.account_name ||
                  account.waba_name ||
                  account.business_name ||
                  "Unknown",
                phoneId: phone.phone_id,
                phoneNumber: phone.phone_number,
                phoneName: phone.phone_name || phone.verified_name || "",
              });
            });
          } else if (account.phone_id) {
            flattened.push({
              id: account.id,
              wabaId: account.waba_id || account.id,
              wabaName:
                account.waba_name ||
                account.account_name ||
                account.business_name ||
                "Unknown",
              phoneId: account.phone_id,
              phoneNumber: account.phone_number,
              phoneName: account.verified_name || account.phone_name || "",
            });
          }
        });

        setPhoneAccounts(flattened);

        // Get current selectedPhoneId from ref (always latest value)
        const currentSelectedPhoneId = selectedPhoneIdRef.current;

        // Only auto-select first phone if no phoneId is currently selected
        // This prevents resetting user's selection
        if (!currentSelectedPhoneId && flattened.length > 0) {
          setSelectedPhoneId(flattened[0].phoneId);
        } else if (currentSelectedPhoneId && flattened.length > 0) {
          // Verify selected phoneId still exists in the list
          const phoneExists = flattened.some((acc) => acc.phoneId === currentSelectedPhoneId);
          if (!phoneExists) {
            // Selected phone no longer exists, select first available
            setSelectedPhoneId(flattened[0].phoneId);
          }
          // If phone exists, don't change selectedPhoneId - preserve user's selection
        }
      } else {
        setPhoneAccounts([]);
      }
    } catch (error) {
      console.error("Error fetching phone accounts:", error);
      setPhoneAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies - uses ref for selectedPhoneId

  /**
   * Set selected phone ID
   * This is the only way to change phoneId - no localStorage involved
   */
  const handleSetPhoneId = useCallback((phoneId) => {
    if (phoneId && phoneId !== selectedPhoneId) {
      console.log(`📱 Phone ID changed: ${selectedPhoneId} -> ${phoneId}`);
      setSelectedPhoneId(phoneId);
    }
  }, [selectedPhoneId]);

  /**
   * Get selected phone account object
   */
  const getSelectedPhoneAccount = useCallback(() => {
    if (!selectedPhoneId) return null;
    return phoneAccounts.find((acc) => acc.phoneId === selectedPhoneId) || null;
  }, [selectedPhoneId, phoneAccounts]);

  /**
   * Fetch phone accounts on mount
   */
  useEffect(() => {
    fetchPhoneAccounts();
  }, [fetchPhoneAccounts]);

  /**
   * Listen for phone account updates from other components
   */
  useEffect(() => {
    const handlePhoneAccountsUpdated = () => {
      fetchPhoneAccounts();
    };

    window.addEventListener("phoneAccountsUpdated", handlePhoneAccountsUpdated);

    return () => {
      window.removeEventListener("phoneAccountsUpdated", handlePhoneAccountsUpdated);
    };
  }, [fetchPhoneAccounts]);

  const value = {
    // State
    phoneAccounts,
    selectedPhoneId,
    selectedPhoneAccount: getSelectedPhoneAccount(),
    loading,
    
    // Actions
    setSelectedPhoneId: handleSetPhoneId,
    fetchPhoneAccounts,
  };

  return (
    <PhoneAccountContext.Provider value={value}>
      {children}
    </PhoneAccountContext.Provider>
  );
};

/**
 * Hook to use PhoneAccount context
 */
export const usePhoneAccount = () => {
  const context = useContext(PhoneAccountContext);
  if (!context) {
    throw new Error("usePhoneAccount must be used within PhoneAccountProvider");
  }
  return context;
};

export default PhoneAccountContext;

